import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Button, Screen, Spinner, Text } from "@ferrivia/ui";
import { useApi, useTracker } from "@ferrivia/auth-web";
import { SYMPTOM_STEP_ID } from "@ferrivia/questionnaire-ui";
import { useTranslation } from "@ferrivia/i18n";
import { INSTRUMENT_DEFINITIONS, type ItemFieldMap } from "@ferrivia/api-contract";
import { useDismissOnBack } from "../hooks/useDismissOnBack";
import { useScreenBackground } from "../hooks/useScreenBackground";
import { useStaticAppHeight } from "../hooks/useStaticAppHeight";
import { ConsentGate } from "./ConsentGate";
import { GeneratingReportScreen } from "./GeneratingReportScreen";
import { HubScreen } from "./HubScreen";
import { ItemEditorScreen } from "./ItemEditorScreen";
import { SectionScreen } from "./SectionScreen";
import { SubmittedScreen } from "./SubmittedScreen";
import { SymptomStepScreen } from "./SymptomStepScreen";
import { useQuestionnaire } from "./useQuestionnaire";
import s from "./QuestionnaireOverlay.module.css";

// The data layer ships exactly one instrument (packages/api-contract's own
// comment on instrumentNameSchema) -- the infusion screener and the
// wellbeing questionnaire are the same instrument by design, not two.
const INSTRUMENT = "health_questionnaire" as const;

const GENERATING_MIN_MS = 2500;

type ReportMoment =
  | { phase: "submitting" }
  | { phase: "generating" }
  | { phase: "ready"; summaryId: string | null };

interface SectionDepthState {
  sectionDepth?: number;
}

/** Mirrors `useDismissOnBack`, but for a stack whose depth changes while the
    overlay stays open the whole time (background -> menstruation_and_pregnancy
    -> ... in one questionnaire session). `useDismissOnBack`'s own boolean
    marker only toggles once per open/close, so it can pop exactly one level
    total -- it never fires again for a depth change that isn't also an
    open/close transition. This keeps a numeric `sectionDepth` in history
    state instead: each level pushes its own entry, so one hardware back pops
    exactly one level, and the app's own back/close controls pop the matching
    entries back off so a later hardware back doesn't see stale depth. */
function useSectionStackDismiss(depth: number, onPopOne: () => void): void {
  const location = useLocation();
  const navigate = useNavigate();
  const ownedDepth = useRef(0);
  const dismiss = useRef(onPopOne);
  dismiss.current = onPopOne;

  const historyDepth = (location.state as SectionDepthState | null)?.sectionDepth ?? 0;

  useEffect(() => {
    if (depth > ownedDepth.current) {
      // Grew by one (a section opened, or continued into the next one): lay
      // down one entry so this level gets its own back press.
      const nextDepth = ownedDepth.current + 1;
      navigate(location.pathname + location.search, {
        state: { ...(location.state as object | null), sectionDepth: nextDepth } satisfies SectionDepthState,
      });
      ownedDepth.current = nextDepth;
      return;
    }

    if (historyDepth < ownedDepth.current) {
      // A hardware/browser back removed one of our entries.
      ownedDepth.current = historyDepth;
      dismiss.current();
      return;
    }

    if (depth < ownedDepth.current) {
      // The app went back on its own (a button, not hardware back) -- take
      // the matching entries back off so a later hardware back doesn't pop
      // stale depth.
      const diff = ownedDepth.current - depth;
      ownedDepth.current = depth;
      navigate(-diff);
    }
  }, [depth, historyDepth, location.pathname, location.search, location.state, navigate]);
}

export interface QuestionnaireOverlayProps {
  open: boolean;
  onClose(): void;
  /** Opens the report just created, once this overlay has closed. */
  onOpenSummary(id: string): void;
}

/** A full-screen overlay rendered inside `AppShell`, inside `BrowserRouter` --
    not a route (no URL of its own, so a half-filled questionnaire is never
    linkable or reloadable into) and not a gate outside the router (there
    would be no history for `useDismissOnBack` to hook, and Android hardware
    back would kill the app instead of closing the flow). See the plan's
    routing section for the three candidates this was chosen over.

    `useDismissOnBack` has to run every render regardless of `open` -- it is
    what notices the open -> closed and closed -> open transitions -- so it
    lives here, in the component AppShell always mounts. The screen-level
    effects (background colour, height freeze) belong only to the moment the
    overlay is actually shown, so they live one component down, which mounts
    and unmounts with `open`. */
export function QuestionnaireOverlay({ open, onClose, onOpenSummary }: QuestionnaireOverlayProps) {
  useDismissOnBack(open, onClose, "questionnaire");
  if (!open) return null;
  return <QuestionnaireOverlayContent onClose={onClose} onOpenSummary={onOpenSummary} />;
}

function QuestionnaireOverlayContent({ onClose, onOpenSummary }: Omit<QuestionnaireOverlayProps, "open">) {
  const { t } = useTranslation("client");
  const tracker = useTracker();
  const api = useApi();
  useScreenBackground("questionnaire", "--color-surface-default");
  useStaticAppHeight();
  const {
    state,
    version,
    answers,
    items,
    itemIndexes,
    sections,
    firstUnfinishedSectionId,
    completeSectionIds,
    visitedSectionIds,
    markSectionVisited,
    hasSavedAnswers,
    save,
    invalidKeys,
    prefilledKeys,
    grantConsent,
    setAnswer,
    saveItem,
    removeItem,
    flush,
    retrySave,
    retry,
    submit,
    symptomCheckDone,
    markSymptomCheckDone,
    ensureStarted,
  } = useQuestionnaire(INSTRUMENT);

  // The riskiest piece of the plan's routing section: nested with the outer
  // useDismissOnBack in QuestionnaireOverlay above. Hardware back pops one
  // section off the stack; popping the last section lands on the hub
  // (`[]`), and hardware back from the hub closes the overlay. `null` means
  // "landing decision not made yet" -- distinct from `[]` (the hub) so the
  // one-time landing effect below can tell them apart.
  const [sectionStack, setSectionStack] = useState<string[] | null>(null);
  const currentSectionId = sectionStack && sectionStack.length > 0 ? sectionStack[sectionStack.length - 1] : null;

  // Sections entered as a revisit -- seen before, or already part-answered --
  // that still had something unanswered at that moment. Their unanswered
  // questions are marked so the client can see exactly what is missing; a
  // first look at a section stays unmarked, nothing there is "missing" yet.
  const [missingShownFor, setMissingShownFor] = useState<ReadonlySet<string>>(new Set());

  // The full-screen item editor, one layer above the current section. Kept
  // here rather than in SectionScreen so it shares the section stack's
  // history entries: hardware back closes it before it pops the section.
  const [itemEditor, setItemEditor] = useState<{ questionKey: string; itemIndex: number | null } | null>(null);
  useEffect(() => {
    setItemEditor(null);
  }, [currentSectionId, state]);

  // Runs once, the moment the data finishes loading: a client with no saved
  // answers starts directly in section 1, a returning client lands on the
  // hub. Guarded on sectionStack === null so a later backToHub()/goBack()
  // landing on [] can't re-trigger it.
  useEffect(() => {
    if (state !== "ready" || sectionStack !== null) return;
    if (hasSavedAnswers) {
      setSectionStack([]);
      return;
    }
    const firstSectionId = sections[0]?.sectionId;
    if (!firstSectionId) {
      setSectionStack([]);
      return;
    }
    setSectionStack([enterStep(firstSectionId)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, sectionStack, hasSavedAnswers]);

  // Flushed on leaving a section, matching the plan's "every dirty key is
  // flushed on blur, on leaving a section, on close, before submit" -- not
  // awaited, so back navigation itself is instant; a failure surfaces on the
  // hub's own resume state instead of blocking the exit. Both the back button
  // and hardware back go through this one path.
  function goBack() {
    void flush();
    setSectionStack((prev) => (prev && prev.length > 0 ? prev.slice(0, -1) : (prev ?? [])));
  }

  useSectionStackDismiss((sectionStack?.length ?? 0) + (itemEditor ? 1 : 0), () => {
    if (itemEditor) setItemEditor(null);
    else goBack();
  });

  /** Bookkeeping for landing on a step, whichever way it was reached.
      Returns the id so callers can push it in the same expression. */
  function enterStep(id: string): string {
    let missingShown = false;
    if (id !== SYMPTOM_STEP_ID) {
      const progress = sections.find((section) => section.sectionId === id);
      const revisit = visitedSectionIds.has(id) || (progress?.answered ?? 0) > 0;
      missingShown = revisit && progress !== undefined && progress.answered < progress.reachable;
      setMissingShownFor((prev) => {
        if (prev.has(id) === missingShown) return prev;
        const next = new Set(prev);
        if (missingShown) next.add(id);
        else next.delete(id);
        return next;
      });
      markSectionVisited(id);
    }
    tracker.track("questionnaire.section_opened", {
      instrument: INSTRUMENT,
      version,
      section_id: id,
      missing_shown: missingShown,
    });
    return id;
  }

  // Sections in order, then the required symptom step, which follows the
  // last section.
  const stepOrder = [...sections.map((section) => section.sectionId), SYMPTOM_STEP_ID];

  /** Where Jatka goes: the next unfinished step after `fromId`, wrapping
      round to earlier ones, never `fromId` itself -- so a client who leaves
      a question blank on purpose is never sent back to the page they are
      on. Null once nothing else is left, which is the hub. */
  function nextUnfinishedStep(fromId: string, symptomDone = symptomCheckDone): string | null {
    const start = stepOrder.indexOf(fromId);
    for (let offset = 1; offset < stepOrder.length; offset++) {
      const id = stepOrder[(start + offset) % stepOrder.length];
      const done = id === SYMPTOM_STEP_ID ? symptomDone : completeSectionIds.has(id);
      if (!done) return id;
    }
    return null;
  }

  function openSection(id: string) {
    setSectionStack([enterStep(id)]);
  }

  function continueFrom(fromId: string, symptomDone = symptomCheckDone) {
    const next = nextUnfinishedStep(fromId, symptomDone);
    if (next === null) {
      setSectionStack([]);
      return;
    }
    setSectionStack((prev) => [...(prev ?? []), enterStep(next)]);
  }

  function goToNextSection() {
    void flush();
    if (currentSectionId !== null) continueFrom(currentSectionId);
  }

  function finishSymptomStep() {
    markSymptomCheckDone();
    continueFrom(SYMPTOM_STEP_ID, true);
  }

  // A section page's close lands on the hub rather than leaving the
  // questionnaire: the hub is where the client sees what is left. Only the
  // hub's own close exits.
  function backToHub() {
    void flush();
    setSectionStack([]);
  }

  // Only a submit made in this session gets the report moment: reopening an
  // already-submitted questionnaire goes straight to the plain end state.
  // Set before the submit so the hub never flashes the end state first.
  const [reportMoment, setReportMoment] = useState<ReportMoment | null>(null);

  async function submitWithReportMoment() {
    setReportMoment({ phase: "submitting" });
    try {
      await submit();
    } catch (err) {
      setReportMoment(null);
      throw err;
    }
    setReportMoment({ phase: "generating" });
    const [summaryId] = await Promise.all([
      api.questionnaireSummaries.list().then(
        (summaries) => summaries[0]?.id ?? null,
        () => null,
      ),
      new Promise((resolve) => setTimeout(resolve, GENERATING_MIN_MS)),
    ]);
    setReportMoment({ phase: "ready", summaryId });
  }

  // The overlay's only exit affordance, the hub's close. A section page's
  // close goes to the hub instead (backToHub).
  function closeFromQuestionnaire() {
    void flush();
    tracker.track("questionnaire.abandoned", {
      instrument: INSTRUMENT,
      version,
      answered_count: sections.reduce((sum, section) => sum + section.answered, 0),
      section_id: currentSectionId,
    });
    onClose();
  }

  // Also covers the one tick between state becoming "ready" and the landing
  // effect above deciding sectionStack -- otherwise the hub would flash
  // before a first-time client is redirected into section 1.
  if (state === "loading" || (state === "ready" && sectionStack === null)) {
    return (
      <Screen className={s.root}>
        <div className={s.centered}>
          <Spinner />
        </div>
      </Screen>
    );
  }

  if (state === "error") {
    return (
      <Screen className={s.root}>
        <div className={s.centered}>
          <Text as="p" size="md" className={s.errorText} role="alert">
            {t("questionnaire.loadFailed", "Kyselyä ei voitu ladata.")}
          </Text>
          <Button onClick={retry}>{t("questionnaire.retryLoad", "Yritä uudelleen")}</Button>
          <Button variant="ghost" onClick={onClose}>
            {t("questionnaire.close", "Sulje")}
          </Button>
        </div>
      </Screen>
    );
  }

  if (state === "consent") {
    return <ConsentGate onAccept={grantConsent} onClose={onClose} />;
  }

  if (state === "submitted" && reportMoment !== null) {
    if (reportMoment.phase !== "ready") {
      return <GeneratingReportScreen />;
    }
    const { summaryId } = reportMoment;
    return (
      <SubmittedScreen
        celebrate
        onClose={onClose}
        onViewReport={
          summaryId === null
            ? undefined
            : () => {
                onOpenSummary(summaryId);
                onClose();
              }
        }
      />
    );
  }

  if (state === "submitted") {
    return <SubmittedScreen onClose={onClose} />;
  }

  // state === "ready"
  if (currentSectionId === SYMPTOM_STEP_ID) {
    return (
      <SymptomStepScreen
        ensureStarted={ensureStarted}
        onSaved={finishSymptomStep}
        onBack={goBack}
        onClose={backToHub}
      />
    );
  }

  if (currentSectionId !== null) {
    const isLastSection = nextUnfinishedStep(currentSectionId) === null;
    const editorGroup = itemEditor
      ? INSTRUMENT_DEFINITIONS[INSTRUMENT].questions.find((question) => question.key === itemEditor.questionKey)
      : undefined;
    let editorInitial: ItemFieldMap = new Map();
    let removeEditedItem: (() => void) | undefined;
    if (itemEditor && itemEditor.itemIndex !== null) {
      const { questionKey, itemIndex } = itemEditor;
      const position = itemIndexes.get(questionKey)?.indexOf(itemIndex) ?? -1;
      editorInitial = items.get(questionKey)?.[position] ?? new Map();
      removeEditedItem = () => {
        removeItem(questionKey, itemIndex);
        tracker.track("questionnaire.item_removed", { instrument: INSTRUMENT, version, question_key: questionKey });
        setItemEditor(null);
      };
    }
    return (
      <>
        <SectionScreen
          // Remount per section: any in-progress local UI state resets clean.
          key={currentSectionId}
          instrument={INSTRUMENT}
          sectionId={currentSectionId}
          isLastSection={isLastSection}
          answers={answers}
          items={items}
          itemIndexes={itemIndexes}
          invalidKeys={invalidKeys}
          prefilledKeys={prefilledKeys}
          showMissing={missingShownFor.has(currentSectionId)}
          save={save}
          onAnswer={setAnswer}
          onOpenItemEditor={(questionKey, itemIndex) => setItemEditor({ questionKey, itemIndex })}
          onRetrySave={retrySave}
          onBack={goBack}
          onNext={goToNextSection}
          onClose={backToHub}
        />
        {itemEditor && editorGroup?.repeating ? (
          <ItemEditorScreen
            key={`${itemEditor.questionKey}-${itemEditor.itemIndex ?? "new"}`}
            group={editorGroup}
            initial={editorInitial}
            mode={itemEditor.itemIndex === null ? "add" : "edit"}
            itemIndex={itemEditor.itemIndex}
            invalidKeys={invalidKeys}
            onSave={(fields) => {
              if (saveItem(itemEditor.questionKey, itemEditor.itemIndex, fields)) {
                tracker.track("questionnaire.item_saved", {
                  instrument: INSTRUMENT,
                  version,
                  question_key: itemEditor.questionKey,
                  mode: itemEditor.itemIndex === null ? "added" : "edited",
                });
              }
              setItemEditor(null);
            }}
            onRemove={removeEditedItem}
            onCancel={() => setItemEditor(null)}
          />
        ) : null}
      </>
    );
  }

  return (
    <HubScreen
      instrument={INSTRUMENT}
      sections={sections}
      firstUnfinishedSectionId={firstUnfinishedSectionId}
      completeSectionIds={completeSectionIds}
      answers={answers}
      items={items}
      symptomCheckDone={symptomCheckDone}
      onOpenSection={openSection}
      onSubmit={submitWithReportMoment}
      onClose={closeFromQuestionnaire}
    />
  );
}
